import {
  DEFAULT_INSPECTION_CHECKLIST,
  INSPECTION_ACTIONS,
  INSPECTION_STATUSES,
  type InspectionAction,
  type InspectionDetail,
  type InspectionStatus,
  type InspectionStepOptions,
  type InspectionSummary,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { ApiError, api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { counterpartiesQuery, projectQuery } from '../../lib/projects';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox, Field, Input, Select, Textarea } from '../../ui/form';
import { KeyValue, Page, PageHeader, Panel } from '../../ui/layout';
import { FileLink, PHOTO_ACCEPT, UploadButton, useProjectDefault, useProjectsWith } from '../psd/shared';
import { Events, SheetPicker } from './SupervisionPage';

/**
 * Освидетельствование скрытых работ (ТН-03): вкладка «Освидетельствование» в разделе «Надзор» и карточка
 * вызова с чек-листом, фото, актом или отказом.
 */

const TONE: Record<InspectionStatus, 'info' | 'success' | 'danger' | undefined> = {
  called: 'info',
  accepted: 'success',
  rejected: 'danger',
  cancelled: undefined,
};

export function InspectionBadge({ status }: { status: InspectionStatus }) {
  return <Badge tone={TONE[status]}>{INSPECTION_STATUSES[status]}</Badge>;
}

/** Дата и время в поле datetime-local по часам браузера */
const localInput = (iso: string) => {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export function InspectionsList({ projectId }: { projectId?: string }) {
  const q = useQuery({
    queryKey: ['inspections', projectId],
    queryFn: () => api<InspectionSummary[]>('/supervision/inspections', { query: { projectId } }),
  });
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  if (!q.data.length)
    return (
      <EmptyState title="Вызовов пока нет">
        Перед закрытием скрытых работ подрядчик вызывает технадзор и авторский надзор на дату и время; надзор
        принимает по чек-листу и подписывает акт или отказывает с причиной.
      </EmptyState>
    );
  return (
    <ul className="flex flex-col gap-2">
      {q.data.map((x) => (
        <li key={x.id}>
          <Link
            to="/supervision/inspections/$inspectionId"
            params={{ inspectionId: x.id }}
            className="flex flex-col gap-1.5 rounded-lg border border-line bg-surface p-4 hover:border-ink-3 md:flex-row md:items-start md:gap-4"
          >
            <span className="num shrink-0 font-medium text-ink md:w-28">{x.number}</span>
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="text-ink [overflow-wrap:anywhere]">{x.workName}</span>
              <span className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">
                {x.projectCode}
                {x.objectName ? ` · ${x.objectName}` : ''}
                {x.inspectors.length ? ` · ${x.inspectors.join(', ')}` : ''}
                {x.nextActivityName ? ` · держит «${x.nextActivityName}»` : ''}
              </span>
            </span>
            <span className="flex flex-wrap items-center gap-1.5 md:justify-end">
              <InspectionBadge status={x.status} />
              <span className="text-[13px] text-ink-2">{dateTime(x.scheduledAt)}</span>
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

type People = Array<{ id: string; fullName: string; external: boolean }>;

/** Подстановка при вызове из отчёта смены (ПР-10) */
export type InspectionPreset = { reportId: string; activityId: string; workName: string };

export function NewInspectionDialog({
  projectId,
  preset,
  onClose,
}: {
  projectId?: string;
  preset?: InspectionPreset;
  onClose: () => void;
}) {
  const me = useMe();
  const staff = me.tenant?.staff ?? false;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { list } = useProjectsWith('supervision.respond');
  const tomorrow = new Date(Date.now() + 86_400_000);
  tomorrow.setHours(10, 0, 0, 0);
  const [f, setF] = useState({
    projectId: projectId && list.some((p) => p.id === projectId) ? projectId : (list[0]?.id ?? ''),
    workName: preset?.workName ?? '',
    objectId: '',
    activityId: preset?.activityId ?? '',
    nextActivityId: '',
    setId: '',
    sheetId: '',
    place: '',
    scheduledAt: localInput(tomorrow.toISOString()),
    inspectorIds: [] as string[],
    contractorId: '',
    checklist: DEFAULT_INSPECTION_CHECKLIST.join('\n'),
    /** Из отчёта смены: приложить фото этой работы (ПР-10) */
    attachReportPhotos: Boolean(preset?.reportId),
  });
  const setProject = useCallback((id: string) => setF((x) => ({ ...x, projectId: id })), []);
  useProjectDefault(list, projectId, f.projectId, setProject);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const project = useQuery({ ...projectQuery(f.projectId), enabled: Boolean(f.projectId) });
  const cps = useQuery({ ...counterpartiesQuery('contractor'), enabled: staff, retry: false });
  const people = useQuery({
    queryKey: ['supervision-people', f.projectId, 'issue'],
    queryFn: () => api<People>('/supervision/people', { query: { projectId: f.projectId, perm: 'issue' } }),
    enabled: Boolean(f.projectId),
  });
  const acts = useQuery({
    queryKey: ['inspection-activities', f.projectId],
    queryFn: () =>
      api<Array<{ id: string; code: string; name: string }>>('/supervision/inspections/activities', {
        query: { projectId: f.projectId },
      }),
    enabled: Boolean(f.projectId),
    retry: false,
  });
  const lines = f.checklist
    .split('\n')
    .map((x) => x.trim())
    .filter((x) => x.length >= 2);
  const m = useMutation({
    mutationFn: () =>
      api<InspectionDetail>('/supervision/inspections', {
        body: {
          projectId: f.projectId,
          workName: f.workName.trim(),
          objectId: f.objectId || null,
          activityId: f.activityId || null,
          nextActivityId: f.nextActivityId || null,
          sheetId: f.sheetId || null,
          reportId: preset?.reportId ?? null,
          attachReportPhotos: preset?.reportId ? f.attachReportPhotos : undefined,
          place: f.place.trim() || null,
          scheduledAt: new Date(f.scheduledAt).toISOString(),
          inspectorIds: f.inspectorIds,
          contractorId: f.contractorId || null,
          checklist: lines,
        },
      }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['inspections'] });
      toast(`Вызов ${d.number} отправлен`);
      onClose();
      navigate({ to: '/supervision/inspections/$inspectionId', params: { inspectionId: d.id } });
    },
  });
  const others = (people.data ?? []).filter((p) => p.id !== me.user?.id);
  return (
    <Dialog
      open
      width="lg"
      onOpenChange={(v) => !v && onClose()}
      title="Вызов на освидетельствование"
      description={
        preset?.reportId
          ? 'Вызванный надзор получит поручение. Фото этой работы из отчёта смены можно приложить сразу; схему и остальные файлы — в карточке вызова.'
          : 'Вызванный надзор получит поручение; фото и исполнительную схему приложите в карточке вызова.'
      }
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="primary"
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={
              !f.projectId ||
              f.workName.trim().length < 3 ||
              !f.inspectorIds.length ||
              !lines.length ||
              !f.scheduledAt
            }
            onClick={() => m.mutate()}
          >
            Вызвать
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Проект" className="sm:col-span-2">
          <Select
            className="h-11 md:h-10"
            value={f.projectId}
            onChange={(e) =>
              setF((x) => ({
                ...x,
                projectId: e.target.value,
                objectId: '',
                activityId: '',
                nextActivityId: '',
                setId: '',
                sheetId: '',
                inspectorIds: [],
              }))
            }
          >
            {list.map((p) => (
              <option key={p.id} value={p.id}>
                {p.code} {p.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Скрытые работы" className="sm:col-span-2">
          <Input
            className="h-11 md:h-10"
            value={f.workName}
            onChange={(e) => set('workName', e.target.value)}
            placeholder="Песчаная подготовка под фундаментную плиту"
          />
        </Field>
        <Field label="Когда">
          <Input
            type="datetime-local"
            className="h-11 md:h-10"
            value={f.scheduledAt}
            onChange={(e) => set('scheduledAt', e.target.value)}
          />
        </Field>
        <Field label="Объект">
          <Select
            className="h-11 md:h-10"
            value={f.objectId}
            onChange={(e) => set('objectId', e.target.value)}
          >
            <option value="">—</option>
            {(project.data?.objects ?? []).map((o) => (
              <option key={o.id} value={o.id}>
                {o.code ? `${o.code} ` : ''}
                {o.name}
              </option>
            ))}
          </Select>
        </Field>
        {acts.data?.length ? (
          <>
            <Field label="Работа графика">
              <Select
                className="h-11 md:h-10"
                value={f.activityId}
                onChange={(e) => set('activityId', e.target.value)}
              >
                <option value="">—</option>
                {acts.data.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.code} {a.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Следующая работа" hint="Не начнётся без подписанного акта">
              <Select
                className="h-11 md:h-10"
                value={f.nextActivityId}
                onChange={(e) => set('nextActivityId', e.target.value)}
              >
                <option value="">—</option>
                {acts.data
                  .filter((a) => a.id !== f.activityId)
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} {a.name}
                    </option>
                  ))}
              </Select>
            </Field>
          </>
        ) : null}
        <SheetPicker
          projectId={f.projectId}
          setId={f.setId}
          sheetId={f.sheetId}
          onChange={(s, sh) => setF((x) => ({ ...x, setId: s, sheetId: sh }))}
        />
        <Field label="Место" hint="Ось, пикет, отметка">
          <Input className="h-11 md:h-10" value={f.place} onChange={(e) => set('place', e.target.value)} />
        </Field>
        {staff ? (
          <Field label="Подрядчик">
            <Select
              className="h-11 md:h-10"
              value={f.contractorId}
              onChange={(e) => set('contractorId', e.target.value)}
            >
              <option value="">—</option>
              {(cps.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.shortName || c.name}
                </option>
              ))}
            </Select>
          </Field>
        ) : null}
        <div className="sm:col-span-2">
          <div className="mb-1.5 text-[13px] text-ink-2">Кого вызвать</div>
          {others.length ? (
            <div className="grid max-h-48 gap-1 overflow-auto rounded-md border border-line p-2 sm:grid-cols-2">
              {others.map((p) => (
                <Checkbox
                  key={p.id}
                  className="min-h-11 items-center md:min-h-0"
                  checked={f.inspectorIds.includes(p.id)}
                  onChange={(e) =>
                    set(
                      'inspectorIds',
                      e.target.checked ? [...f.inspectorIds, p.id] : f.inspectorIds.filter((x) => x !== p.id),
                    )
                  }
                  label={`${p.fullName}${p.external ? ' (внешний)' : ''}`}
                />
              ))}
            </div>
          ) : (
            <p className="text-[13px] text-ink-2">В проекте нет надзора с правом выдавать замечания.</p>
          )}
        </div>
        <Field label="Чек-лист приёмки" hint="Каждый пункт с новой строки" className="sm:col-span-2">
          <Textarea rows={6} value={f.checklist} onChange={(e) => set('checklist', e.target.value)} />
        </Field>
        {preset?.reportId ? (
          <div className="sm:col-span-2">
            <Checkbox
              className="min-h-11 items-center md:min-h-0"
              checked={f.attachReportPhotos}
              onChange={(e) => set('attachReportPhotos', e.target.checked)}
              label="Приложить фото этой работы из отчёта смены"
            />
          </div>
        ) : null}
      </div>
      <ErrorBox error={m.error} className="mt-4" />
    </Dialog>
  );
}

export function InspectionPage() {
  const { inspectionId } = useParams({ strict: false }) as { inspectionId: string };
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['inspection', inspectionId],
    queryFn: () => api<InspectionDetail>(`/supervision/inspections/${inspectionId}`),
  });
  const [act, setAct] = useState<InspectionAction | null>(null);
  useCrumbs(
    q.data
      ? [
          { label: q.data.projectCode, to: `/supervision?project=${q.data.projectId}&tab=inspections` },
          { label: 'Освидетельствование', to: `/supervision?tab=inspections&project=${q.data.projectId}` },
          { label: q.data.number },
        ]
      : null,
    q.error,
  );
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    qc.invalidateQueries({ queryKey: ['inspections'] });
  };
  if (q.isPending)
    return (
      <Page>
        <Spinner />
      </Page>
    );
  if (q.isError)
    return (
      <Page>
        <ErrorBox error={q.error} />
      </Page>
    );
  const d = q.data;
  const main = d.actions.filter((a) => a !== 'comment');
  return (
    <Page>
      <PageHeader
        title={`Освидетельствование ${d.number}`}
        subtitle={<span className="[overflow-wrap:anywhere]">{d.workName}</span>}
        actions={
          <>
            {main.map((a, i) => (
              <Button
                key={a}
                size="lg"
                variant={i === 0 ? 'primary' : a === 'cancel' || a === 'reject' ? 'danger' : 'secondary'}
                onClick={() => setAct(a)}
              >
                {INSPECTION_ACTIONS[a]}
              </Button>
            ))}
            <Button size="lg" variant="ghost" onClick={() => setAct('comment')}>
              Комментарий
            </Button>
          </>
        }
      >
        <div className="mt-2">
          <InspectionBadge status={d.status} />
        </div>
      </PageHeader>
      <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
        <div className="flex min-w-0 flex-col gap-4">
          <Panel title="Вызов">
            <KeyValue
              items={[
                { label: 'Когда', value: dateTime(d.scheduledAt) },
                { label: 'Объект', value: d.objectName },
                { label: 'Место', value: d.place },
                {
                  label: 'Отчёт смены',
                  value: d.report ? (
                    <Link
                      to="/site/review"
                      search={{ project: d.projectId, report: d.report.id }}
                      className="inline-flex min-h-11 items-center underline md:min-h-0"
                    >
                      {d.report.reportDay}, смена {d.report.shiftKey}
                    </Link>
                  ) : null,
                },
                {
                  label: 'Лист ПСД',
                  value:
                    d.sheetId && d.setCode ? (
                      <Link
                        to="/psd/sheets/$sheetId"
                        params={{ sheetId: d.sheetId }}
                        className="inline-flex min-h-11 items-center underline md:min-h-0"
                      >
                        {d.setCode}, лист {d.sheetNumber}
                      </Link>
                    ) : null,
                },
                {
                  label: 'Работа графика',
                  value: d.activityName ? (
                    <Link
                      to="/schedule/$projectId"
                      params={{ projectId: d.projectId }}
                      className="inline-flex min-h-11 items-center underline md:min-h-0"
                    >
                      {d.activityName}
                    </Link>
                  ) : null,
                },
                {
                  label: 'Следующая работа',
                  value: d.nextActivityName
                    ? `${d.nextActivityName}${d.status === 'accepted' ? ' — можно начинать' : ' — не начинать до подписанного акта'}`
                    : null,
                },
                { label: 'Вызван', value: d.inspectors.join(', ') || null },
                { label: 'Подрядчик', value: d.contractorName },
                { label: 'Вызвал', value: d.createdBy },
                {
                  label: d.status === 'accepted' ? 'Акт подписал' : 'Решение',
                  value: d.decidedAt ? `${d.decidedBy ?? '—'}, ${dateTime(d.decidedAt)}` : null,
                },
                { label: 'Причина', value: d.decision },
              ]}
            />
            {d.status === 'accepted' ? (
              <p className="mt-3 text-[13px] text-ink-2">
                Акт подписан в портале под учётной записью надзора; подпись ЭЦП добавится после подключения
                НУЦ РК.
              </p>
            ) : null}
          </Panel>
          <Panel title="Чек-лист приёмки">
            <ul className="flex flex-col gap-2 text-[14px]">
              {d.checklist.map((c, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: пункты чек-листа могут совпадать текстом
                <li key={i} className="flex items-start gap-2">
                  <span
                    className={c.ok === true ? 'text-success' : c.ok === false ? 'text-danger' : 'text-ink-3'}
                  >
                    {c.ok === true ? '✓' : c.ok === false ? '✗' : '○'}
                  </span>
                  <span className="min-w-0 [overflow-wrap:anywhere]">
                    {c.text}
                    {c.note ? <span className="text-ink-2"> — {c.note}</span> : null}
                  </span>
                </li>
              ))}
            </ul>
          </Panel>
          <Panel
            title="Фото и исполнительная схема"
            actions={
              d.status === 'called' && main.length ? (
                <>
                  <UploadButton
                    url={`/supervision/inspections/${d.id}/files`}
                    fields={{ stage: 'before' }}
                    accept={PHOTO_ACCEPT}
                    label="Фото работ"
                    onDone={refresh}
                  />
                  <UploadButton
                    url={`/supervision/inspections/${d.id}/files`}
                    fields={{ stage: 'doc' }}
                    accept={PHOTO_ACCEPT}
                    label="Схема"
                    onDone={refresh}
                  />
                </>
              ) : null
            }
          >
            {d.photos.length ? (
              <ul className="flex flex-col gap-1 text-[14px]">
                {d.photos.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-2">
                    <Badge>{p.stage === 'doc' ? 'схема' : 'фото'}</Badge>
                    <FileLink id={p.id} name={p.name} kind="supervision/inspections" />
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[14px] text-ink-2">Файлов нет.</p>
            )}
          </Panel>
        </div>
        <Panel title="Ход">
          <Events
            events={d.events.map((e) => ({ ...e, fromStatus: null, toStatus: null }))}
            statuses={INSPECTION_STATUSES}
          />
        </Panel>
      </div>
      {act ? (
        <InspectionActionDialog d={d} action={act} onClose={() => setAct(null)} onDone={refresh} />
      ) : null}
    </Page>
  );
}

function InspectionActionDialog({
  d,
  action,
  onClose,
  onDone,
}: {
  d: InspectionDetail;
  action: InspectionAction;
  onClose: () => void;
  onDone: () => void;
}) {
  const [comment, setComment] = useState('');
  const [when, setWhen] = useState(localInput(d.scheduledAt));
  const [checks, setChecks] = useState(d.checklist.map(() => ({ ok: false, note: '' })));
  const decide = action === 'accept' || action === 'reject';
  const needComment = action === 'reject' || action === 'cancel' || action === 'comment';
  const allOk = checks.every((c) => c.ok);
  const qc = useQueryClient();
  const chooseSteps = action === 'accept' && Boolean(d.activityId);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const steps = useQuery({
    queryKey: ['inspection-step-options', d.id],
    queryFn: () => api<InspectionStepOptions>(`/supervision/inspections/${d.id}/steps`),
    enabled: chooseSteps,
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  const options = steps.data?.steps ?? [];
  const allStepsClosed = options.length > 0 && options.every((s) => s.doneAt);
  const selected = options.filter((s) => !s.doneAt && selectedIds.includes(s.id));
  const stepsReady =
    !chooseSteps ||
    (steps.isSuccess &&
      !steps.isFetching &&
      steps.data.activityId === d.activityId &&
      (options.length === 0 || allStepsClosed || selected.length > 0));
  const m = useMutation({
    mutationFn: () => {
      if (!stepsReady) throw new Error('Обновите список и выберите подтверждаемые шаги.');
      return api<InspectionDetail>(`/supervision/inspections/${d.id}/action`, {
        body: {
          action,
          comment: comment.trim() || null,
          ...(decide ? { checks: checks.map((c) => ({ ok: c.ok, note: c.note.trim() || null })) } : {}),
          ...(action === 'reschedule' ? { scheduledAt: new Date(when).toISOString() } : {}),
          ...(chooseSteps && selected.length ? { stepIds: selected.map((s) => s.id) } : {}),
          ...(chooseSteps && allStepsClosed ? { stepsAlreadyClosed: true } : {}),
        },
      });
    },
    onSuccess: () => {
      if (action === 'accept') {
        void qc.invalidateQueries({ queryKey: ['activity-steps', d.activityId] });
        void qc.invalidateQueries({ queryKey: ['schedule', d.projectId] });
        void qc.invalidateQueries({ queryKey: ['inspection-step-options', d.id] });
      }
      onDone();
      toast(`${d.number}: ${INSPECTION_ACTIONS[action].toLowerCase()} — готово`);
      onClose();
    },
    onError: (error) => {
      if (chooseSteps && error instanceof ApiError && [403, 404, 409].includes(error.status)) {
        setSelectedIds([]);
        void steps.refetch();
      }
    },
  });
  return (
    <Dialog
      open
      width={decide ? 'lg' : undefined}
      onOpenChange={(v) => !v && onClose()}
      title={`${INSPECTION_ACTIONS[action]}: ${d.number}`}
      description={
        action === 'accept'
          ? 'Отметьте все пункты чек-листа — акт подписывается, когда всё выполнено.'
          : action === 'reject'
            ? 'Отметьте, что не выполнено, и укажите причину.'
            : undefined
      }
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant={action === 'reject' || action === 'cancel' ? 'danger' : 'primary'}
            className="h-11 md:h-10"
            loading={m.isPending}
            disabled={
              (needComment && !comment.trim()) ||
              (action === 'accept' && (!allOk || !stepsReady)) ||
              (action === 'reschedule' && !when)
            }
            onClick={() => m.mutate()}
          >
            {INSPECTION_ACTIONS[action]}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {chooseSteps ? (
          <section className="flex flex-col gap-2" aria-label="Подтверждаемые шаги графика">
            <h3 className="font-semibold">Какие шаги подтверждает акт</h3>
            <p className="text-[13px] text-ink-2">
              Выберите выполненные шаги предъявляемой работы. Остальные останутся открытыми.
            </p>
            {steps.isFetching ? <p role="status">Загружаем шаги…</p> : null}
            <ErrorBox error={steps.error} />
            {steps.isSuccess ? (
              options.length ? (
                <>
                  <ul className="flex flex-col gap-2">
                    {options.map((s) => (
                      <li key={s.id} className="rounded-md border border-line p-2">
                        <Checkbox
                          className="min-h-11 items-center [overflow-wrap:anywhere]"
                          checked={Boolean(s.doneAt) || selected.some((x) => x.id === s.id)}
                          disabled={Boolean(s.doneAt) || steps.isFetching || m.isPending}
                          onChange={(e) =>
                            setSelectedIds((ids) =>
                              e.target.checked ? [...ids, s.id] : ids.filter((id) => id !== s.id),
                            )
                          }
                          label={`${s.name} · ${String(s.weight).replace('.', ',')} %${s.doneAt ? ' — уже закрыт' : ''}`}
                        />
                      </li>
                    ))}
                  </ul>
                  <p className="text-[13px] text-ink-2" role="status">
                    {allStepsClosed
                      ? 'Все актовые шаги уже закрыты. Примите документ по чек-листу: процент и основания закрытия шагов сохранятся.'
                      : selected.length
                        ? `Выбрано шагов: ${selected.length}. Вес: ${String(selected.reduce((sum, s) => sum + s.weight, 0)).replace('.', ',')} %.`
                        : 'Выберите хотя бы один шаг для принятия акта.'}
                  </p>
                </>
              ) : (
                <p className="text-[13px] text-ink-2">
                  У этой работы нет актовых шагов. Примите акт по чек-листу.
                </p>
              )
            ) : null}
            <Button
              variant="ghost"
              className="self-start min-h-11"
              disabled={steps.isFetching || m.isPending}
              onClick={() => {
                setSelectedIds([]);
                void steps.refetch();
              }}
            >
              Обновить шаги
            </Button>
          </section>
        ) : null}
        {decide ? (
          <ul className="flex flex-col gap-2">
            {d.checklist.map((c, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: пункты чек-листа могут совпадать текстом
              <li key={i} className="flex flex-col gap-1 rounded-md border border-line p-2">
                <Checkbox
                  className="min-h-11 items-center md:min-h-0"
                  checked={checks[i]!.ok}
                  onChange={(e) =>
                    setChecks((x) => x.map((y, j) => (j === i ? { ...y, ok: e.target.checked } : y)))
                  }
                  label={c.text}
                />
                {!checks[i]!.ok ? (
                  <Input
                    aria-label={`Замечание к пункту ${i + 1}`}
                    className="h-11 md:h-9"
                    placeholder="Что не так"
                    value={checks[i]!.note}
                    onChange={(e) =>
                      setChecks((x) => x.map((y, j) => (j === i ? { ...y, note: e.target.value } : y)))
                    }
                  />
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {action === 'reschedule' ? (
          <Field label="Новое время">
            <Input
              type="datetime-local"
              className="h-11 md:h-10"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
          </Field>
        ) : null}
        <Field label={needComment ? 'Комментарий (обязательно)' : 'Комментарий'}>
          <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <ErrorBox error={m.error} />
      </div>
    </Dialog>
  );
}
